import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import lodash from 'lodash';
import { debounceTime } from 'rxjs/operators';

import { TimeService } from '@bayclubs/utils';

import { CategoryModel, ClubModel, ContextModel } from '@bayclubs/group-classes-employee-api';
import { DataFilteringService, FiltersStorageService } from '../../../../shared/shared.module';
import { GroupClassesSecurityService } from './../../../../infrastructure/infrastructure.module';
import { YesNotFilterOption } from './../../../../infrastructure/models/search-criteria';

@Component({
    selector: 'app-group-classes-search-criteria',
    templateUrl: './group-classes-search-criteria.component.html',
    styleUrls: ['./group-classes-search-criteria.component.scss'],
    standalone: false
})
export class GroupClassesSearchCriteriaComponent implements OnInit {
    @Input()
    public form: UntypedFormGroup;
    public advancedFilters = false;
    public context: ContextModel;

    public clubs: ClubModel[];
    public categories: CategoryModel[];
    public onlineOptions: YesNotFilterOption[] = [];

    public isVisibleToConnectOptions: YesNotFilterOption[] = [];

    constructor(
        private activatedRoute: ActivatedRoute,
        private filtersStorageService: FiltersStorageService,
        private router: Router,
        private security: GroupClassesSecurityService,
        private filteringService: DataFilteringService,
        private translate: TranslateService,
        private time: TimeService
    ) {
        this.context = lodash.cloneDeep(activatedRoute.snapshot.data.context);
    }

    ngOnInit() {
        this.initOnlineOptions();
        this.initIsVisibleToConnectOptions();
        this.subscribeToQueryParamChanges();
        this.subscribeToFormChanges();

        this.initClubs();
    }

    private initOnlineOptions() {
        this.onlineOptions = [
            {
                code: 'true',
                name: this.translate.instant('General.Yes'),
            },
            {
                code: 'false',
                name: this.translate.instant('General.No'),
            },
        ];
    }

    private initIsVisibleToConnectOptions() {
        this.isVisibleToConnectOptions = [
            {
                code: 'true',
                name: this.translate.instant('General.Yes'),
            },
            {
                code: 'false',
                name: this.translate.instant('General.No'),
            },
        ];
    }

    private initClubs() {
        this.security.filterClubsForViewClasses(this.context.clubs).subscribe(filtered => {
            this.clubs = filtered;
            this.assignAvailableCategoriesByClubs();
        });
    }

    private assignAvailableCategoriesByClubs(clubIds: string[] = null): void {
        this.categories = this.filteringService.getAvailableCategoriesByClubs<CategoryModel, string>(clubIds, this.context);
    }

    public clearFilters() {
        this.form.setValue({
            name: null,
            dateTo: null,
            dateFrom: this.time.toBcDate(this.time.todayInDefaultTimeZone()),
            clubs: null,
            categories: null,
            statuses: null,
            instructor: null,
            onlyBookingRequired: false,
            online: null,
            isVisibleToConnect: null,
            substitutedOnly: false,
        });

        this.mergeQueryParam({
            dateTo: null,
            dateFrom: null,
            clubs: null,
            categories: null,
            statuses: null,
            instructor: null,
            onlyBookingRequired: null,
            online: null,
            isVisibleToConnect: null,
            substitutedOnly: null,
        });
    }

    private subscribeToQueryParamChanges() {
        this.activatedRoute.queryParamMap.pipe(debounceTime(20)).subscribe(params => {
            const instructorNameParam: string = params.get('instructor');
            if (instructorNameParam != null) {
                const instructorControl = this.form.controls['instructor'];
                instructorControl.setValue(instructorNameParam, { emitEvent: false });
            }

            const dateFromParam: string = params.get('dateFrom');
            if (dateFromParam) {
                const dateFromControl = this.form.controls['dateFrom'];
                const dateObj = { value: dateFromParam };
                dateFromControl.setValue(dateObj, { emitEvent: false });
            }

            const dateToParam: string = params.get('dateTo');
            if (dateToParam) {
                const dateToControl = this.form.controls['dateTo'];
                const dateObj = { value: dateToParam };
                dateToControl.setValue(dateObj, { emitEvent: false });
            }

            const clubs: string[] = params.getAll('clubs');
            const clubsControl = this.form.controls['clubs'];
            clubsControl.setValue(clubs || [], { emitEvent: false });
            this.assignAvailableCategoriesByClubs(clubs);

            const categoriesCodes: string[] = params.getAll('categories');
            const categories: CategoryModel[] = this.context.categories.filter(c => lodash.includes(categoriesCodes, c.code));
            const categoriesControl = this.form.controls['categories'];
            categoriesControl.setValue(categories || [], { emitEvent: false });

            const statuses: string[] = params.getAll('statuses');
            const statusesControl = this.form.controls['statuses'];
            statusesControl.setValue(statuses || [], { emitEvent: false });

            const advancedFiltersParam: string = params.get('filters');
            const advancedFilters: boolean = advancedFiltersParam === '1';
            this.advancedFilters = advancedFilters;

            const onlyBookingRequiredParam: string = params.get('onlyBookingRequired');
            const onlyBookingRequiredControl = this.form.controls['onlyBookingRequired'];
            const onlyBookingRequiredValue = onlyBookingRequiredParam === 'true';
            onlyBookingRequiredControl.setValue(onlyBookingRequiredValue, { emitEvent: false });

            const onlineParam: string = params.get('online');
            const onlineControl = this.form.controls['online'];
            onlineControl.setValue(onlineParam, { emitEvent: false });

            const isVisibleToConnectParam: string = params.get('isVisibleToConnect');
            const isVisibleToConnect = this.form.controls['isVisibleToConnect'];
            isVisibleToConnect.setValue(isVisibleToConnectParam, { emitEvent: false });

            const substitutedOnlyParam: string = params.get('substitutedOnly');
            const substitutedOnlyValue = substitutedOnlyParam === 'true';
            const substitutedOnlyControl = this.form.controls['substitutedOnly'];
            substitutedOnlyControl.setValue(substitutedOnlyValue, { emitEvent: false });
        });
    }

    private subscribeToFormChanges() {
        this.form.controls['instructor'].valueChanges.subscribe(value => {
            this.mergeQueryParam({ instructor: value });
        });

        this.form.controls['dateFrom'].valueChanges.subscribe(value => {
            if (value) {
                this.mergeQueryParam({ dateFrom: value.value });
            } else {
                this.mergeQueryParam({ dateFrom: null });
            }
        });

        this.form.controls['dateTo'].valueChanges.subscribe(value => {
            if (value) {
                this.mergeQueryParam({ dateTo: value.value });
            } else {
                this.mergeQueryParam({ dateTo: null });
            }
        });

        this.form.controls['clubs'].valueChanges.subscribe(value => {
            this.mergeQueryParam({ clubs: value });
        });

        this.form.controls['categories'].valueChanges.subscribe(value => {
            let categoriesParam: Params;

            if (value) {
                const categoriesCodes = value.map(item => item.code);

                categoriesParam = { categories: categoriesCodes };
            } else {
                categoriesParam = [];
            }

            this.mergeQueryParam(categoriesParam);
        });

        this.form.controls['statuses'].valueChanges.subscribe(value => {
            this.mergeQueryParam({ statuses: value });
        });

        this.form.controls['onlyBookingRequired'].valueChanges.subscribe(value => {
            this.mergeQueryParam({ onlyBookingRequired: value });
        });

        this.form.controls['online'].valueChanges.subscribe(value => {
            this.mergeQueryParam({ online: value });
        });

        this.form.controls['isVisibleToConnect'].valueChanges.subscribe(value => {
            this.mergeQueryParam({ isVisibleToConnect: value });
        });

        this.form.controls['substitutedOnly'].valueChanges.subscribe(value => {
            this.mergeQueryParam({ substitutedOnly: value });
        });
    }

    private mergeQueryParam(params: Params): void {
        this.router
            .navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: params,
                queryParamsHandling: 'merge',
            })
            .then(() => (this.filtersStorageService.listUrl = this.router.url));
    }

    public changeTypeOfFilters(): void {
        this.advancedFilters = !this.advancedFilters;

        const params = { filters: this.advancedFilters ? '1' : '0' };
        this.mergeQueryParam(params);
    }

    public onlyBookingRequiredChanged(onlyBookingRequired: boolean) {
        this.form.get('onlyBookingRequired').setValue(onlyBookingRequired);
    }

    public onlySubstitutedChanged(substitutedOnly: boolean) {
        this.form.get('substitutedOnly').setValue(substitutedOnly);
    }
}
